import React, { useState, useMemo } from 'react';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { ModalOverlay, ModalContent, ModalHeader, ButtonGroup, WarningBox } from '../styles';
import { Button } from '../../../components/common/Button';

const HistoryModal = ({ onClose, onConfirm, limitDays = 30 }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Calcula a data limite (exatamente 1 ano atrás a partir de hoje)
    const minDateLimit = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - limitDays); // Subtrai os dias do plano
        return date.toISOString().split('T')[0];
    }, [limitDays]);

    // Verifica se a data de início é maior que a data fim
    const isDateInverted = useMemo(() => {
        if (startDate && endDate) {
            return new Date(startDate) > new Date(endDate);
        }
        return false;
    }, [startDate, endDate]);

    // Verifica se a data de início é mais antiga que 1 ano
    const isTooOld = useMemo(() => {
        if (startDate) {
            return new Date(startDate) < new Date(minDateLimit);
        }
        return false;
    }, [startDate, minDateLimit]);

    // Validação Geral do Botão
    const isValid = useMemo(() => {
        if (isDateInverted || isTooOld) return false;
        if (!startDate && !endDate) return true; 
        if (startDate && endDate) return true;
        
        return false; 
    }, [startDate, endDate, isDateInverted, isTooOld]);

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>
                    <h3>Sincronizar Dados</h3>
                    <FaTimes onClick={onClose} />
                </ModalHeader>
                
                <p style={{color: '#555', marginBottom: '1rem', lineHeight: '1.5'}}>
                    Seu plano permite acessar histórico de até <strong>{limitDays} dias</strong>.<br/>
                    Deixar em branco busca os dados mais recentes.
                </p>

                <WarningBox>
                    <FaExclamationTriangle />
                    <div>
                        <strong>Atenção:</strong>
                        <ul>
                            <li>Selecionar períodos muito longos pode resultar em demora ou falha na conexão.</li>
                            <li>Existe um limite de segurança de <strong>1 sincronização por hora</strong>.</li>
                        </ul>
                    </div>
                </WarningBox>

                <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                    <label>
                        Data Início:
                        <input 
                            type="date" 
                            min={minDateLimit} // UX: Impede selecionar no calendário (em browsers modernos)
                            value={startDate} 
                            onChange={e => setStartDate(e.target.value)} 
                            style={{
                                width: '100%', 
                                padding: '0.5rem', 
                                border: (isDateInverted || isTooOld) ? '1px solid red' : '1px solid #ccc',
                                borderRadius: '4px'
                            }} 
                            required={!!endDate} 
                        />
                    </label>
                    <label>
                        Data Fim:
                        <input 
                            type="date" 
                            min={minDateLimit}
                            value={endDate} 
                            onChange={e => setEndDate(e.target.value)} 
                            style={{
                                width: '100%', 
                                padding: '0.5rem', 
                                border: isDateInverted ? '1px solid red' : '1px solid #ccc', 
                                borderRadius: '4px'
                            }} 
                            required={!!startDate}
                        />
                    </label>
                    
                    {/* Mensagens de Erro Condicionais */}
                    {isDateInverted && (
                        <span style={{color: 'red', fontSize: '0.85rem', marginTop: '-0.5rem'}}>
                            A data de início não pode ser posterior à data final.
                        </span>
                    )}
                    
                    {isTooOld && (
                        <WarningBox style={{marginTop: 0, padding: '0.5rem'}}>
                            <small style={{color: '#d8000c'}}>
                                Data fora do limite do seu plano ({limitDays} dias).
                            </small>
                        </WarningBox>
                    )}
                </div>

                <ButtonGroup style={{marginTop: '1.5rem'}}>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button 
                        onClick={() => onConfirm(startDate, endDate)} 
                        disabled={!isValid} 
                    >
                        {(!startDate && !endDate) ? 'Sincronizar Recentes (30d)' : 'Sincronizar Período'}
                    </Button>
                </ButtonGroup>
            </ModalContent>
        </ModalOverlay>
    );
};

export default HistoryModal;